import * as Sentry from '@sentry/react'
import type { ReactNode } from 'react'
import { createContext, useCallback, useEffect, useRef, useState } from 'react'

import { env } from '@/env'
import { useFrontChatIdentity } from '@/hooks/use-front-chat-identity'
import useAuth from '@/hooks/useAuth'

const FRONT_CHAT_SCRIPT_SRC =
  'https://chat-assets.frontapp.com/v1/chat.bundle.js'

export type FrontChatContextType = {
  isReady: boolean
  unreadCount: number
  openChat: (customFields?: Record<string, string | number | boolean>) => void
}

const FrontChatContext = createContext<FrontChatContextType>({
  isReady: false,
  unreadCount: 0,
  openChat: () => {},
})

// Load the Front Chat bundle once. Resolves when window.FrontChat is available.
function loadFrontChatScript() {
  return new Promise<void>((resolve, reject) => {
    if (window.FrontChat) {
      resolve()
      return
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${FRONT_CHAT_SCRIPT_SRC}"]`
    )

    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Front Chat script'))
      )
      return
    }

    const script = document.createElement('script')
    script.src = FRONT_CHAT_SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Front Chat script'))

    document.body.appendChild(script)
  })
}

function FrontChatProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const initializedRef = useRef(false)
  // Name of the merchant currently sent to Front Chat, to skip redundant inits.
  const sentMerchantRef = useRef<string | undefined>(undefined)
  // Whether the chat window is open, to defer re-inits that would disrupt it.
  const isWindowVisibleRef = useRef(false)
  const hasPendingReinitRef = useRef(false)
  // Stable reference to the re-init routine for use inside SDK callbacks,
  // avoiding a circular dependency with `initFrontChat`.
  const reinitRef = useRef<() => void>(() => { })

  const { data: identity } = useFrontChatIdentity()
  const { merchantSelected } = useAuth()
  const merchantName = merchantSelected?.name

  // Latest identity/merchant kept in refs so the (re)init callback can read
  // fresh values without being re-created on every change.
  const identityRef = useRef(identity)
  identityRef.current = identity
  const merchantNameRef = useRef(merchantName)
  merchantNameRef.current = merchantName

  const initFrontChat = useCallback(() => {
    const name = merchantNameRef.current
    sentMerchantRef.current = name

    window.FrontChat?.('init', {
      chatId: env.VITE_FRONT_CHAT_ID,
      useDefaultLauncher: false,
      userId: identityRef.current?.userId,
      userHash: identityRef.current?.userHash,
      shouldShowWindowOnLaunch: false,
      name,
      // SDK commands (onUnreadChange, etc.) must only be registered after the
      // widget has finished initializing, otherwise they are silently ignored.
      onInitCompleted: () => {
        setIsReady(true)

        window.FrontChat?.('onUnreadChange', ({ unread_count }) =>
          // While the chat is open the user is already reading, so never show a
          // badge for messages that arrive in that state.
          setUnreadCount(isWindowVisibleRef.current ? 0 : unread_count)
        )

        window.FrontChat?.('onWindowVisibilityChanged', ({ is_window_visible }) => {
          const visible = Boolean(is_window_visible)
          isWindowVisibleRef.current = visible

          if (visible) {
            // Opening the chat means the user is reading the messages, so clear
            // the badge immediately instead of waiting for an unread event.
            setUnreadCount(0)
            return
          }

          // Flush a merchant change that arrived while the chat was open.
          if (hasPendingReinitRef.current) {
            hasPendingReinitRef.current = false
            reinitRef.current()
          }
        })
      },
    })
  }, [])

  // Re-init requires shutting the widget down first: `init` only runs once per
  // page load unless `shutdown` is called. Session is preserved.
  const reinitFrontChat = useCallback(() => {
    window.FrontChat?.('shutdown', { clearSession: false })
    initFrontChat()
  }, [initFrontChat])
  reinitRef.current = reinitFrontChat

  // First load: download the bundle and initialize once identity is available.
  useEffect(() => {
    if (initializedRef.current || !identity) return
    initializedRef.current = true

    loadFrontChatScript()
      .then(() => initFrontChat())
      .catch((error) => {
        initializedRef.current = false
        Sentry.captureException(error)
      })
  }, [identity, initFrontChat])

  // Re-initialize when the selected merchant changes after the first init, so
  // new conversations always carry the current store. `init` can only run once
  // per page load, hence the `shutdown` first. Deferred while the chat is open.
  useEffect(() => {
    if (!isReady || merchantName === sentMerchantRef.current) return

    if (isWindowVisibleRef.current) {
      hasPendingReinitRef.current = true
      return
    }

    reinitFrontChat()
  }, [merchantName, isReady, reinitFrontChat])

  const openChat = useCallback(
    (customFields?: Record<string, string | number | boolean>) => {
      if (customFields && window.FrontChat) {
        window.FrontChat('init', {
          chatId: env.VITE_FRONT_CHAT_ID,
          useDefaultLauncher: false,
          userId: identityRef.current?.userId,
          userHash: identityRef.current?.userHash,
          shouldShowWindowOnLaunch: false,
          name: merchantNameRef.current,
          customFields,
          onInitCompleted: () => {
            window.FrontChat?.('show')
          },
        })
        return
      }

      window.FrontChat?.('show')
    },
    []
  )

  return (
    <FrontChatContext.Provider value={{ isReady, unreadCount, openChat }}>
      {children}
    </FrontChatContext.Provider>
  )
}

export { FrontChatContext, FrontChatProvider }
